import {
  ComponentFactoryResolver,
  Type,
  ViewContainerRef
} from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { NonArchivedSubProject } from 'src/app/model/project/non-archived-sub-project';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { OnView } from 'src/app/model/tabs/on-view';
import { ClientAdjustmentDetailComponent } from '../client-adjustment-detail/client-adjustment-detail.component';
import { ClientPendingDetailComponent } from '../client-pending-detail/client-pending-detail.component';
import { ClientProjectDetailTab } from './client-project-detail-tab';
import { OnViewUpdatable } from 'src/app/model/tabs/on-view-updatable/on-view-updatable';
import { RecupJobDetailComponent } from 'src/app/pages/atelier/recup-job-detail/recup-job-detail.component';

export class ClientProjectDetailTabManager implements OnViewUpdatable<string> {
  tabs: ClientProjectDetailTab[] = [];
  activeTab: ClientProjectDetailTab;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private projectDetailTabContent: ViewContainerRef
  ) {}

  prepareSubProjectTabs(
    projectId: number,
    nonArchivedSubProjects: NonArchivedSubProject[],
    type: string,
    title: string,
    form: boolean = false,
    forAtelier: boolean = false
  ): void {
    const baseProject = nonArchivedSubProjects.find(
      s => s.type === 'BaseProject'
    );
    const baseProjectId = baseProject != null ? baseProject.id : null;

    if (CollectionHelper.isPopulated(nonArchivedSubProjects)) {
      // [adjustmentCount, soldRentedCount, repoCount]
      const adjustmentCounts = [0, 0, 0];
      const moreThanOneOfAdjustmentType = this.moreThanOneOfAdjustmentType(
        nonArchivedSubProjects
      );

      nonArchivedSubProjects.forEach(sp => {
        const componentType: Type<OnView<string>> =
          sp.type === SUB_PROJECT_TYPES.BASE_PROJECT
            ? forAtelier
              ? RecupJobDetailComponent
              : ClientPendingDetailComponent
            : ClientAdjustmentDetailComponent;
        const tabTitle = this.getTabTitle(
          projectId,
          sp,
          adjustmentCounts,
          moreThanOneOfAdjustmentType
        );
        const spTitle = this.getTabTitle(projectId, sp, adjustmentCounts, [
          false,
          false,
          false
        ]);
        const inputs = {
          subProjectId: sp.id,
          projectId: projectId,
          form: false,
          type: sp.type.toLocaleLowerCase(),
          title: spTitle,
          executed: sp.executedForClient,
          baseProjectId,
          forAtelier
        };
        const tab = new ClientProjectDetailTab(
          tabTitle,
          componentType,
          inputs,
          sp.executedForClient
        );
        this.tabs.push(tab);
      });
    }

    if (form) {
      let tabTitle = title;
      if (CollectionHelper.isPopulated(nonArchivedSubProjects)) {
        const adjCount = nonArchivedSubProjects.filter(
          sub => sub.type === SUB_PROJECT_TYPES[type.toUpperCase()]
        ).length;
        tabTitle = adjCount > 0 ? tabTitle + ' #' + adjCount : tabTitle;
      }
      const inputs = {
        projectId: projectId,
        subProjectId: null,
        form: true,
        type: type,
        title: title,
        executed: false,
        baseProjectId
      };
      const tab = new ClientProjectDetailTab(
        title[0].toUpperCase() + title.substr(1).toLowerCase(),
        ClientAdjustmentDetailComponent,
        inputs,
        false
      );
      this.tabs.push(tab);
    }
  }

  // toReturn[0]: more than one Adjustment
  // toReturn[1]: more than one SoldRented
  // toReturn[2]: more than one Repo
  private moreThanOneOfAdjustmentType(
    subProjects: NonArchivedSubProject[]
  ): boolean[] {
    let toReturn = [false, false, false];
    let adjustmentSeen = false;
    let soldRentedSeen = false;
    let repoSeen = false;

    for (const subProject of subProjects) {
      switch (subProject.type) {
        case SUB_PROJECT_TYPES.BASE_PROJECT:
          break;
        case SUB_PROJECT_TYPES.ADJUSTMENT:
          toReturn[0] = adjustmentSeen;
          adjustmentSeen = true;
          break;
        case SUB_PROJECT_TYPES.SOLDRENTED:
          toReturn[1] = soldRentedSeen;
          soldRentedSeen = true;
          break;
        case SUB_PROJECT_TYPES.REPO:
          toReturn[2] = repoSeen;
          repoSeen = true;
          break;
        default:
          throw new Error(`unknown subproject type: ${subProject.type}`);
      }
    }

    return toReturn;
  }

  private getTabTitle(
    projectId: number,
    subProject: NonArchivedSubProject,
    adjustmentCounts: number[],
    moreThanOneOfAdjustmentType: boolean[]
  ): string {
    switch (subProject.type) {
      case SUB_PROJECT_TYPES.BASE_PROJECT:
        return `Job #${projectId}`;
      case SUB_PROJECT_TYPES.ADJUSTMENT:
        return moreThanOneOfAdjustmentType[0]
          ? `Aanpassing #${++adjustmentCounts[0]}`
          : 'Aanpassing';
      case SUB_PROJECT_TYPES.SOLDRENTED:
        return moreThanOneOfAdjustmentType[1]
          ? `Sticker VV #${++adjustmentCounts[1]}`
          : 'Sticker VV';
      case SUB_PROJECT_TYPES.REPO:
        return moreThanOneOfAdjustmentType[2]
          ? `Weghalen #${++adjustmentCounts[2]}`
          : 'Weghalen';
      default:
        throw new Error(`unknown subproject type: ${subProject.type}`);
    }
  }

  activateTab(tab: ClientProjectDetailTab): void {
    if (tab.component == null || tab.component.hostView.destroyed) {
      tab.createComponentView(
        this.componentFactoryResolver,
        this.projectDetailTabContent
      );
    }
    this.activeTab = tab;
    this.projectDetailTabContent.detach();
    this.projectDetailTabContent.clear();
    this.projectDetailTabContent.insert(tab.component.hostView);
    tab.component.instance.onView(this);
    tab.component.changeDetectorRef.markForCheck();
  }

  activateTabMatching(
    subProjectId: number,
    type: string,
    form: boolean = false
  ) {
    let lookup = null;
    if (form) {
      lookup = this.tabs.find(
        tab =>
          tab.inputs != null &&
          tab.inputs.form === true &&
          tab.inputs.type === type
      );
    } else {
      lookup = this.tabs.find(
        tab => tab.inputs != null && tab.inputs.subProjectId === subProjectId
      );
    }
    if (lookup != null) {
      this.activateTab(lookup);
    }
  }

  onViewUpdate(input: string): void {
    const baseProjectTab = CollectionHelper.isPopulated(this.tabs)
      ? this.tabs.find(t => t.componentClass === ClientPendingDetailComponent)
      : null;
    if (baseProjectTab != null) {
      baseProjectTab.oldPlatformIdTitle = ` (#${input})`;
    }
  }

  destroy(): void {
    this.tabs.forEach(tab => tab.closeTab());
    this.tabs.length = 0;
  }
}
